import * as React from "react";
import { useNavigate } from "react-router-dom";
import env from "@shared/api/env";
import { getSupabaseAuthHeaders } from "@shared/api/supabase-auth-headers";
import supabase from "@shared/api/supabase";
import type { Tables } from "@shared/database.types";
import { JSONTable, type ColumnDef } from "@shared/json-table";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { MoreVertical, Plus, EyeOff, Eye } from "lucide-react";
import { useWorkspace } from "@/context/workspace";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateFlowsTree } from "@features/flows/lib/use-flows";
import { FlowEditSheet } from "@features/flows/components/FlowEditSheet";
import { createFlow, updateFlow, deleteFlow } from "@features/flows/api/flows";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

type Flow = Tables<"flows">;

export default function FlowsListPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { active: ws } = useWorkspace();

  const [headers, setHeaders] = React.useState<Record<string, string> | null>(null);
  const [showArchived, setShowArchived] = React.useState(false);
  const [tableVersion, setTableVersion] = React.useState(0);

  const [openSheet, setOpenSheet] = React.useState(false);
  const [edit, setEdit] = React.useState<Flow | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState<Flow | null>(null);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      const h = await getSupabaseAuthHeaders();
      const withWs = ws?.id ? { ...h, 'X-Workspace-ID': ws.id } : h;
      if (mounted) setHeaders(withWs);
    })();
    return () => { mounted = false; };
  }, [ws?.id]);

  // Refresh headers on auth token refresh/sign-in to avoid stale Authorization
  React.useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        const h = await getSupabaseAuthHeaders();
        const withWs = ws?.id ? { ...h, 'X-Workspace-ID': ws.id } : h;
        setHeaders(withWs);
      }
    });
    return () => { subscription.unsubscribe(); };
  }, [ws?.id]);

  const bump = () => setTableVersion((v) => v + 1);
  const onCreated = async (values: { name: string; description?: string | null }) => {
    if (!ws?.id) return;
    try {
      // Use Supabase SDK path to satisfy RLS (sets created_by/updated_by + workspace_id)
      const row = await createFlow({ workspace_id: ws.id, name: values.name, description: values.description });
      bump();
      // Invalidate flows tree in the sidebar
      invalidateFlowsTree();
      // also clear react-query caches if any (no-op if unused)
      try { qc.invalidateQueries({ queryKey: ["flows"] }); } catch {}
      toast.success("Flow created");
      // Redirect to the new flow canvas (schedule to avoid UI race conditions)
      setTimeout(() => nav(`/flows/${encodeURIComponent(String(row.id))}`), 0);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to create flow");
      throw err;
    }
  };
  const onUpdated = async (values: { name: string; description?: string | null }) => {
    if (!edit) return;
    try {
      await updateFlow(edit.id, values);
      bump();
      invalidateFlowsTree();
      try { qc.invalidateQueries({ queryKey: ["flows"] }); } catch {}
      toast.success("Flow updated");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to update flow");
      throw err;
    }
  };
  const onDelete = async (row: Flow) => {
    setPendingDelete(row);
    setConfirmOpen(true);
  };

  // Relative time formatter for Updated column
  const formatRelative = (iso: string | null) => {
    if (!iso) return "";
    const date = new Date(iso);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const sec = Math.round(diffMs / 1000);
    const min = Math.round(sec / 60);
    const hr = Math.round(min / 60);
    const day = Math.round(hr / 24);
    const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
    if (Math.abs(sec) < 60) return rtf.format(sec, "second");
    if (Math.abs(min) < 60) return rtf.format(min, "minute");
    if (Math.abs(hr) < 24) return rtf.format(hr, "hour");
    return rtf.format(day, "day");
  };

  const baseUrl = `${env.SUPABASE_URL}/rest/v1`;
  const resource = "flows";
  const columns: ColumnDef<Flow>[] = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      enableSort: true,
      filter: { type: "text", placeholder: "e.g. ETL daily" },
      cell: (row) => (
        <button
          className="underline hover:no-underline"
          onClick={(e) => { e.stopPropagation(); nav(`/flows/${encodeURIComponent(String(row.id))}`); }}
          title="Open on canvas"
        >
          {String(row.name)}
        </button>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      enableSort: false,
      filter: { type: "text" },
      cell: (row) => <span className="line-clamp-2">{String(row.description ?? "")}</span>,
    },
    { id: "revision", header: "Rev", accessorKey: "revision", enableSort: true, size: 80 },
    {
      id: "updated_at",
      header: "Updated",
      accessorKey: "updated_at",
      enableSort: true,
      size: 220,
      cell: (row) => (
        <span title={new Date(String(row.updated_at ?? "")).toLocaleString()}>{formatRelative(String(row.updated_at ?? ""))}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      size: 56,
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={(e) => { e.preventDefault(); setEdit(row as any); setOpenSheet(true); }}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem className="text-destructive"
              onClick={(e) => { e.preventDefault(); void onDelete(row as any); }}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="max-w-screen-xl w-full mx-auto">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button
          className="gap-2"
          onClick={() => { setEdit(null); setOpenSheet(true); }}
        >
          <Plus className="h-4 w-4" />
          New Flow
        </Button>

        <Button
          variant="outline"
          className="gap-2"
          onClick={() => setShowArchived((v) => !v)}
          title={showArchived ? "Hide archived" : "Show archived"}
        >
          {showArchived ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {showArchived ? "Hide archived" : "Show archived"}
        </Button>
      </div>

      {/*
        Note: we duplicate X-Workspace-ID in headers even though the Supabase SDK injects it globally,
        because JSONTable uses raw fetch against PostgREST.
      */}
      <JSONTable<Flow>
        id={`flows.${ws?.id ?? 'no-ws'}.${tableVersion}`}
        baseUrl={baseUrl}
        resource={resource}
        select="id,name,description,revision,updated_at,is_archived,workspace_id"
        columns={columns}
        headers={headers ?? undefined}
        staticFilters={[
          ...(ws?.id ? [{ id: "workspace_id", op: "eq" as const, value: ws.id }] : []),
          ...(!showArchived ? [{ id: "is_archived", op: "eq" as const, value: false }] : []),
        ]}
        globalSearch={{ columns: ["name", "description"], placeholder: "Search name or description…" }}
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pageSize: 20,
          sort: [{ id: "updated_at", desc: true }],
          columnVisibility: { actions: true, description: true, revision: true, updated_at: true },
        }}
        queryOptions={{
          enabled: Boolean(headers && ws?.id),
          staleTime: 10_000,
          retry: 1,
        }}
      />

      <FlowEditSheet
        open={openSheet}
        onOpenChange={setOpenSheet}
        mode={edit ? "edit" : "create"}
        initial={edit ? { name: edit.name, description: (edit.description as any) ?? "" } : undefined}
        onSubmit={edit ? onUpdated : onCreated}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => { setConfirmOpen(o); if (!o) setPendingDelete(null); }}
        title={pendingDelete ? `Delete “${pendingDelete.name}”?` : "Delete"}
        description="This action cannot be undone. This will permanently delete the flow."
        confirmText="Delete"
        variant="destructive"
        onConfirm={async () => {
          if (!pendingDelete) return;
          try {
            await deleteFlow(pendingDelete.id);
            bump();
            invalidateFlowsTree();
            try { qc.invalidateQueries({ queryKey: ["flows"] }); } catch {}
            toast.success("Flow deleted");
          } catch (err: any) {
            toast.error(err?.message ?? "Failed to delete flow");
            throw err;
          } finally {
            setPendingDelete(null);
          }
        }}
      />
    </div>
  );
}
